import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

const BRAND_COLORS = {
  vector: '#F5C842',
  pebbles: '#F5A0C0',
  milo: '#3CC9A0',
};
const DEFAULT_BG = '#534AB7';

const CHAR_EMOJIS = { pebbles: '🐰', milo: '🦕', vector: '🐱' };

function charKey(name) {
  return String(name || '').toLowerCase();
}

export default function StoryTransitionCard({
  completedStory,
  nextStory,
  delaySeconds = 5,
  onContinue,
  onChoose,
}) {
  const timerRef = useRef(null);
  const nextKey = charKey(nextStory?.character);
  const bgColor = BRAND_COLORS[nextKey] || DEFAULT_BG;
  const completedEmoji = CHAR_EMOJIS[charKey(completedStory?.character)] || '📖';

  useEffect(() => {
    timerRef.current = setTimeout(() => onContinue?.(), delaySeconds * 1000);
    return () => clearTimeout(timerRef.current);
  }, [delaySeconds, onContinue]);

  const handleContinue = () => {
    clearTimeout(timerRef.current);
    onContinue?.();
  };

  const handleChoose = () => {
    clearTimeout(timerRef.current);
    onChoose?.();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100dvh',
        zIndex: 100,
        backdropFilter: 'blur(4px)',
        backgroundColor: bgColor,
        opacity: undefined, // framer handles opacity on the outer div
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 24px',
        boxSizing: 'border-box',
        // ~0.95 opacity via a semi-transparent overlay child so framer's opacity still works
      }}
    >
      {/* use an inner solid layer at 0.95 so framer fade-in still transitions cleanly */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: bgColor,
          opacity: 0.95,
          pointerEvents: 'none',
        }}
      />

      <style>{`
        @keyframes stc-fillBar {
          from { width: 0%; }
          to   { width: 100%; }
        }
      `}</style>

      {/* All content sits above the solid layer */}
      <div style={{ position: 'relative', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>

        {/* ── TOP: completed story ── */}
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 28, lineHeight: 1, marginBottom: 8 }}>{completedEmoji}</div>

          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 280, damping: 18, delay: 0.12 }}
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: '#4CAF50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 12px',
            }}
          >
            <span style={{ color: '#fff', fontSize: 18, fontWeight: 900, lineHeight: 1 }}>✓</span>
          </motion.div>

          <p style={{
            margin: 0,
            fontSize: 14,
            fontStyle: 'italic',
            color: '#fff',
            lineHeight: 1.4,
            maxWidth: 260,
          }}>
            "{completedStory?.title}"
          </p>
        </div>

        {/* ── DIVIDER ── */}
        <div style={{
          width: '70%',
          height: 1,
          background: 'rgba(255,255,255,0.2)',
          marginBottom: 20,
        }} />

        {/* ── MIDDLE: next story ── */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.6)',
            marginBottom: 14,
          }}>
            Next up...
          </div>

          {nextStory?.image ? (
            <motion.img
              src={nextStory.image}
              alt={nextStory.title || ''}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              style={{
                width: 200,
                height: 150,
                borderRadius: 12,
                objectFit: 'cover',
                boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                display: 'block',
                margin: '0 auto',
              }}
            />
          ) : (
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              style={{
                width: 200,
                height: 150,
                borderRadius: 12,
                background: 'rgba(255,255,255,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                fontSize: 40,
              }}
            >
              📖
            </motion.div>
          )}

          <div style={{
            fontSize: 18,
            fontWeight: 600,
            color: '#fff',
            marginTop: 12,
            lineHeight: 1.3,
          }}>
            {nextStory?.title}
          </div>
          <div style={{
            fontSize: 13,
            color: 'rgba(255,255,255,0.7)',
            marginTop: 4,
          }}>
            with {nextStory?.character}
          </div>
        </div>

        {/* ── BOTTOM: progress bar + buttons ── */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
          {/* Progress bar */}
          <div style={{
            width: 200,
            height: 3,
            borderRadius: 2,
            background: 'rgba(255,255,255,0.2)',
            overflow: 'hidden',
            position: 'relative',
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              background: '#fff',
              animationName: 'stc-fillBar',
              animationDuration: `${delaySeconds}s`,
              animationTimingFunction: 'linear',
              animationFillMode: 'forwards',
            }} />
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              type="button"
              onClick={handleContinue}
              style={{
                padding: '12px 18px',
                borderRadius: 12,
                border: 'none',
                background: '#fff',
                color: bgColor,
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
                whiteSpace: 'nowrap',
              }}
            >
              ▶ Keep watching
            </button>
            <button
              type="button"
              onClick={handleChoose}
              style={{
                padding: '12px 18px',
                borderRadius: 12,
                border: '2px solid rgba(255,255,255,0.65)',
                background: 'transparent',
                color: '#fff',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              Choose a story
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
